/* eslint-disable max-len */
import {
  AnimatedSpinner,
  Button,
  Dialog,
  Form,
  IconButton,
  IconPawPrint,
  IconPetSitting,
  IconSearch,
  LayoutGridContainer,
  LayoutGridItem,
  LayoutVerticalSpacer,
  useDialogHeadingId,
  useLargeFromMediaQuery,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';

import { Banner } from '~/common/components/Banner/Banner';
import { validateSearchWidget } from '~/common/components/SearchBarWidget/MobileSearchWidgetWrapper/utils';
import {
  DEFAULT_SERVICE_TYPE_DESCRIPTION,
  SERVICE_TYPE_DESCRIPTION_MAPPING,
} from '~/common/constants/search';
import { trackEvent } from '~/components/analytics/analytics';
import {
  CLICKED_EXPANDED_SEARCH_DESKTOP,
  CLICKED_EXPANDED_SEARCH_MOBILE,
  EDIT_SEARCH_CLICKED,
  UPDATE_FILTER_PETS_EVENT_NAME,
  UPDATE_FILTER_SERVICE_EVENT_NAME,
} from '~/components/analytics/constants';
import { DOM_CONTAINER_ID, SERVICE_OPTIONS_V2 } from '~/components/constants';
import { ProductReview } from '~/components/pages/Home/Hero/components/ProductReview';
import { getEventProperties } from '~/components/pages/SearchResults/tracking/getUpdatedSearchTrackingProperties';

import styles from './AdvancedSearchFormAB.module.css';
import {
  DESKTOP_SCROLL_POSITION,
  MOBILE_SCROLL_POSITION,
  RWB_SERVICE_NAMES,
  SEARCH_FILTERS_FORM_ID,
  SERVICE_AND_BOOKING_TYPE_FORM_ID,
} from '../../constants';
import { BookingTypeField } from '../../ui/BookingType/BookingTypeField';
import { DateSelector } from '../../ui/DateSelector/DateSelector';
import { DialogFormField } from '../../ui/DialogFormField/DialogFormField';
import { DynamicHeader } from '../../ui/DynamicHeader/DynamicHeader';
import { DynamicPetSitterCount } from '../../ui/DynamicPetSitterCount/DynamicPetSitterCount';
import { ExpandedFilters } from '../../ui/ExpandedFilters/ExpandedFilters';
import { LocationField } from '../../ui/Location/LocationField';
import { ServiceTypeField } from '../../ui/ServiceType/ServiceTypeField';
import { StickyMobileEditSearch } from '../../ui/StickyMobileEditSearch/StickyMobileEditSearch';
import { YourPetsField } from '../../ui/YourPets/YourPetsField';
import {
  checkChronology,
  checkPets,
  transformPetSelectionToString,
  transformServiceOptionsToString,
} from '../../utils';
import { AdvancedFiltersMainDialog } from '../AdvancedFilters/AdvancedFiltersMainDialog';
import { AdvancedFiltersMinReviewsDialog } from '../AdvancedFilters/AdvancedFiltersMinReviewsDialog';
import { AdvancedFiltersPriceDialog } from '../AdvancedFilters/AdvancedFiltersPriceDialog';

import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

type Props = {
  currentValues: SearchFilters;
  isLoading: boolean;
  serviceSlug: string;
  sittersCount: number;
};

const AdvancedSearchFormAB = ({
  currentValues,
  isLoading,
  serviceSlug,
  sittersCount,
}: Props): ReactElement => {
  const { values, handleSubmit } = useFormikContext<SearchFilters>();

  const [isSearchAlertShown, setIsSearchAlertShown] = useState(false);

  const dialogHeadingId = useDialogHeadingId();
  const isLargeFromViewport = useLargeFromMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [isMobileExtended, setIsMobileExtended] = useState(true);

  const [isFullFormFilled, setIsFullFormFilled] = useState(false);
  const [isSearchButtonPressed, setIsSearchButtonPressed] = useState(true);

  const [isSticky, setIsSticky] = useState(false);
  const [isStickyMobileSearch, setIsStickyMobileSearch] = useState(false);
  const previousScrollY = useRef(0);
  const extendedModeEnabled = useRef(false);
  const chronologyIsFine = checkChronology(values);
  const petsIsFine = checkPets(values);

  useEffect(() => {
    const getFullFormFilled = async (): Promise<void> => {
      const validation = await validateSearchWidget(values);

      setIsFullFormFilled(!validation);

      if (isSearchButtonPressed && !!validation) {
        setIsSearchButtonPressed(false);
      }
    };

    getFullFormFilled();
  }, [values, isSearchButtonPressed]);

  const showMobileSearchExperiment = !isLargeFromViewport;

  const shouldShowSearchAlert = !(chronologyIsFine && petsIsFine);

  useEffect(() => {
    const handleScroll = (): void => {
      const currentScrollY = window.scrollY;
      const scrollUp = currentScrollY < previousScrollY.current;

      previousScrollY.current = currentScrollY;

      // isSticky is used to determine if sticky behaviour is required/active.
      // isStickyMobileSearch is used to determine if the StickyMobileSearch
      // component should be shown (for tablet and mobile screen sizes).

      // MOBILE_SCROLL_POSITION and DESKTOP_SCROLL_POSITION are used to
      // determine the scroll position at which the sticky behaviour should
      // be applied. These values are different because the sticky behaviour
      // is applied to search bars of different heights.

      if (!isLargeFromViewport && scrollUp && currentScrollY > MOBILE_SCROLL_POSITION) {
        setIsSticky(true);
        setIsStickyMobileSearch(true);
      } else if (isLargeFromViewport && currentScrollY > DESKTOP_SCROLL_POSITION) {
        setIsSticky(true);
        setIsStickyMobileSearch(false);
      } else {
        setIsSticky(false);
        setIsStickyMobileSearch(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isLargeFromViewport, previousScrollY]);

  const handleScrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleDialogClose = (): void => {
    setIsDialogOpen(!isDialogOpen);
    extendedModeEnabled.current = false;
  };

  const handleMobileExtendedSubmit = (): void => {
    setIsMobileExtended(!isMobileExtended);
    extendedModeEnabled.current = false;
  };

  /*
   * Define if app should follow a scenario where expanded
   * search filters for mobile should be rendered
   */
  const shouldRenderExpandedMobileFilters =
    !isMediumFromViewport &&
    isMobileExtended &&
    (shouldShowSearchAlert || extendedModeEnabled.current);

  const onSubmit = (): void => {
    handleSubmit();
    setIsSearchButtonPressed(true);

    if (shouldRenderExpandedMobileFilters) {
      trackEvent(CLICKED_EXPANDED_SEARCH_MOBILE, getEventProperties(values));
      handleMobileExtendedSubmit();
    }
  };

  const handleEditSearch = (): void => {
    setIsSearchButtonPressed(false);
    handleScrollToTop();

    trackEvent(EDIT_SEARCH_CLICKED);
  };

  if (isSearchAlertShown !== shouldShowSearchAlert) {
    setIsSearchAlertShown(shouldShowSearchAlert);
  }

  if (shouldShowSearchAlert) {
    extendedModeEnabled.current = true;
  }

  /*
   * Define if app should follow a scenario where expanded
   * search filters for desktop (dialog) should be rendered
   */
  const shouldRenderExpandedDesktopFilters =
    isMediumFromViewport && (shouldShowSearchAlert || extendedModeEnabled.current);

  const isServiceTypeDisplayValueHasDescription = false;

  const serviceDescription =
    SERVICE_TYPE_DESCRIPTION_MAPPING[serviceSlug] || DEFAULT_SERVICE_TYPE_DESCRIPTION;

  const dialogAddServiceFormlabel = showMobileSearchExperiment
    ? 'Which service are you looking for?'
    : 'Choose your service';

  const dialogAddServiceFormPlaceolder = showMobileSearchExperiment
    ? 'Select the care they need'
    : 'Choose service';

  const dialogAddPetsFormlabel = showMobileSearchExperiment
    ? 'How many pets need caring for?'
    : 'Add your pets';

  const dialogAddPetsFormPlaceolder = showMobileSearchExperiment
    ? 'How many pets do you have?'
    : 'Add pets';

  const addLocationPlaceholder = showMobileSearchExperiment
    ? 'Add your street address or suburb'
    : 'Search location';

  const addDaysPlaceholder = showMobileSearchExperiment ? 'Which days?' : 'Add days';

  const addDaysStartingPlaceholder = showMobileSearchExperiment ? 'Starting from' : 'Start week';

  const searchAlertLabel =
    'Add your dates and pets to find the best sitters available for your needs.';

  const optionsToShow = SERVICE_OPTIONS_V2;

  const renderLocationField = (): ReactElement => (
    <LocationField isLabelVisuallyHidden placeholder={addLocationPlaceholder} />
  );

  const renderPetsDialogField = (): ReactElement => (
    <DialogFormField
      displayValue={transformPetSelectionToString(values.petTypes, true)}
      handleSubmit={(): void => trackEvent(UPDATE_FILTER_PETS_EVENT_NAME)}
      icon={<IconPawPrint />}
      isLabelVisuallyHidden
      label={dialogAddPetsFormlabel}
      placeholder={dialogAddPetsFormPlaceolder}
      visuallyHiddenLabel="To change the pet selection, click the field or press ENTER key to launch the pet field in a dialog."
    >
      <YourPetsField />
    </DialogFormField>
  );

  const renderServiceDialogField = (): ReactElement => (
    <DialogFormField
      displayValue={transformServiceOptionsToString(
        optionsToShow,
        values.service.type,
        values.bookingType,
        isServiceTypeDisplayValueHasDescription
      )}
      handleSubmit={(): void => trackEvent(UPDATE_FILTER_SERVICE_EVENT_NAME)}
      icon={<IconPetSitting />}
      isDialogCtaHidden={showMobileSearchExperiment}
      isLabelVisuallyHidden
      label={dialogAddServiceFormlabel}
      placeholder={dialogAddServiceFormPlaceolder}
      visuallyHiddenLabel="To change the service type, click the field or press ENTER key to launch the service field in a dialog."
    >
      <Form id={SERVICE_AND_BOOKING_TYPE_FORM_ID} label={{ id: dialogHeadingId }}>
        <ServiceTypeField />
        {isLargeFromViewport && <BookingTypeField />}
      </Form>
    </DialogFormField>
  );

  const showBookingTypeField =
    showMobileSearchExperiment && RWB_SERVICE_NAMES.includes(values.service.type);

  return (
    <>
      {isFullFormFilled && isSearchButtonPressed && !isLargeFromViewport && (
        <div className={`${styles.sticky}`}>
          <StickyMobileEditSearch
            handleScrollToFilters={handleScrollToTop}
            isLoading={isLoading}
            onEditSearch={handleEditSearch}
            onSubmit={onSubmit}
          />
        </div>
      )}
      {!(isFullFormFilled && isSearchButtonPressed && !isLargeFromViewport) && (
        <div
          className={
            isSticky && !isStickyMobileSearch
              ? styles.sticky
              : `${styles.root} ${shouldRenderExpandedMobileFilters && styles.rootRounded}`
          }
        >
          <Form
            hasHtml5Validation={false}
            hasTighterSpacing
            id={SEARCH_FILTERS_FORM_ID}
            isGridRowGapDisabled={false}
            label={{ id: dialogHeadingId }}
          >
            <div className={styles.formContainer}>
              {shouldRenderExpandedMobileFilters && (
                <div className={styles.expandedMobileHeader}>
                  <DynamicHeader searchFilters={currentValues} serviceSlug={serviceSlug}>
                    <LayoutVerticalSpacer space="none">
                      <p className={styles.headerDescription}>{serviceDescription}</p>
                    </LayoutVerticalSpacer>
                  </DynamicHeader>
                </div>
              )}
              <div className={styles.searchForm}>
                {showMobileSearchExperiment ? (
                  <div className={styles.breakPoints}>{renderPetsDialogField()}</div>
                ) : (
                  <div className={styles.breakPoints}>{renderLocationField()}</div>
                )}
                {showMobileSearchExperiment ? (
                  <div className={styles.breakPoints}>{renderLocationField()}</div>
                ) : (
                  renderServiceDialogField()
                )}
                {/* By design Dates and Pets fields should swap its places
              for different screen sizes */}
                {isMediumFromViewport && (
                  <div className={styles.dates}>
                    <DateSelector
                      dateFormat="fancy"
                      isCalendarIconHidden={false}
                      isLabelVisuallyHidden
                      placeholder={addDaysPlaceholder}
                      rwbStartingFromPlaceholder={addDaysStartingPlaceholder}
                    />
                  </div>
                )}
                {showMobileSearchExperiment ? (
                  <div className={styles.breakPoints}>{renderServiceDialogField()}</div>
                ) : (
                  renderPetsDialogField()
                )}
                {showBookingTypeField && (
                  <div className={styles.bookingType}>
                    <BookingTypeField />
                  </div>
                )}
                {/* By design Dates and Pets fields should swap its places
              for different screen sizes */}
                {!isMediumFromViewport && (
                  <div className={styles.dates}>
                    <DateSelector
                      dateFormat="fancy"
                      isCalendarIconHidden={false}
                      isCheckboxLegendVisuallyHidden
                      isLabelVisuallyHidden
                      placeholder="Which days?"
                      rwbStartingFromPlaceholder="Starting from"
                      weekDaySelectorLabel="Which day(s) would you like to book?"
                    />
                  </div>
                )}

                <div className={styles.searchBtn}>
                  <Button
                    iconLeading={
                      isLargeFromViewport ? (
                        isLoading ? (
                          <AnimatedSpinner size="medium" />
                        ) : undefined
                      ) : isLoading ? (
                        <AnimatedSpinner size="medium" />
                      ) : (
                        <IconSearch size="medium" />
                      )
                    }
                    isDisabled={isLoading}
                    isFullBleed
                    isSubmit
                    label="Search"
                    onClick={onSubmit}
                  />
                </div>
                <div className={styles.searchIconButton}>
                  <IconButton isDisabled={isLoading} label="Update search" onClick={onSubmit}>
                    {isLoading ? <AnimatedSpinner /> : <IconSearch />}
                  </IconButton>
                </div>
              </div>
              {shouldRenderExpandedMobileFilters && <ProductReview />}
              <div className={styles.divider} />
              <div className={styles.filterRoot}>
                <div className={styles.filters}>
                  <AdvancedFiltersMainDialog
                    handleAdvancedFiltersSubmit={onSubmit}
                    isLoading={isLoading}
                    isPriceSliderShown
                    isReviewFilterEnabled
                  />
                  <AdvancedFiltersPriceDialog
                    handleAdvancedFiltersSubmit={onSubmit}
                    isLoading={isLoading}
                  />
                  <AdvancedFiltersMinReviewsDialog
                    handleAdvancedFiltersSubmit={onSubmit}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </div>
          </Form>
          {shouldRenderExpandedDesktopFilters && (
            <Dialog
              cta={{
                iconLeading: <IconSearch />,
                label: 'Find a sitter',
                onClick: (): void => {
                  onSubmit();
                  trackEvent(CLICKED_EXPANDED_SEARCH_DESKTOP, getEventProperties(values));
                  handleDialogClose();
                },
                size: 'large1x',
              }}
              domContainerId={DOM_CONTAINER_ID}
              heading="Find the perfect sitter"
              isOpen={isDialogOpen}
              onRequestClose={handleDialogClose}
            >
              <ExpandedFilters />
            </Dialog>
          )}
        </div>
      )}
      {isFullFormFilled && isSearchButtonPressed && !isLargeFromViewport && (
        <div className={styles.emptyTop} />
      )}

      <LayoutGridContainer>
        <LayoutGridItem placement={{ small: [2, 12], medium: [3, 11], large: [4, 10] }}>
          <>
            {isSearchAlertShown && (
              <LayoutVerticalSpacer space="large1x">
                <Banner description={searchAlertLabel} />
              </LayoutVerticalSpacer>
            )}
          </>

          <div className={styles.dynamicHeaderContainer}>
            <DynamicHeader searchFilters={currentValues} serviceSlug={serviceSlug}>
              <DynamicPetSitterCount
                isChronologyFine={chronologyIsFine}
                serviceSlug={serviceSlug}
                sittersCount={sittersCount}
              />
            </DynamicHeader>
            {/* Removed as a requirement in WEB-2020 */}
            {/* <SortFilter onChange={onSubmit} /> */}
          </div>
        </LayoutGridItem>
      </LayoutGridContainer>
    </>
  );
};

export { AdvancedSearchFormAB };
